import React, { useEffect, useState } from 'react';
import { X, Loader2, Eye, EyeOff, Info, AlertTriangle, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import axiosInstance from '../utils/axiosInstance';
import { previewMark } from './carrierMarkPreview';
import ExperimentNotice from './ExperimentNotice';

/**
 * Checkout preview — what this cart would actually see at checkout, and why.
 *
 * The backend does not reimplement the rules: it signs a real Shopify
 * carrier-service payload and calls the real view, then reports the trace that
 * view wrote. So the top list IS the customer's shipping options, and the
 * bottom list is every rate that exists but was withheld, with the reason.
 */
const money = (v, cur = 'EUR') =>
  v === 0 ? 'Free' : `${cur === 'EUR' ? '€' : cur + ' '}${Number(v || 0).toFixed(2)}`;

export default function CheckoutPreviewModal({ open, onClose, items, countryIso2, country, postalCode, orderTotal, locale }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showHidden, setShowHidden] = useState(true);
  // "What would we quote for Belgium?" — BE and NL are not switched on at
  // checkout, so a plain preview dead-ends at the kill switch and tells ops
  // nothing. This re-runs as if the country were live, always labelled.
  const [testMode, setTestMode] = useState(false);
  // Street for a shipping experiment: postcode + street pick the A/B arm, as
  // for a customer. Committed on Enter / blur so typing does not re-run.
  const [street, setStreet] = useState('');
  const [streetDraft, setStreetDraft] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(null); setData(null);
    axiosInstance.post('api/checkout-preview/', {
      items, country_iso2: countryIso2, country,
      postal_code: postalCode || '', order_total: orderTotal || 0, locale: locale || null,
      test_mode: testMode, address1: street,
    })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, items, countryIso2, country, postalCode, orderTotal, locale, testMode, street]);

  // Each open starts from the truth: what a customer sees today.
  useEffect(() => { if (open) setTestMode(false); }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const ctx = data?.context;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8"
         onClick={onClose}>
      <div className="w-full max-w-3xl rounded-xl border border-border bg-card shadow-xl"
           onClick={e => e.stopPropagation()}>

        {/* header */}
        <div className="flex items-start justify-between gap-3 border-b border-border p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-primary shrink-0" />
              <h2 className="text-base font-semibold">Checkout preview</h2>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The real carrier service, run on this cart. These are the shipping options the customer would see.
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* context strip */}
        {ctx && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border bg-muted/30 px-4 py-2 text-[11px] text-muted-foreground">
            <span><strong className="text-foreground">{ctx.country}</strong>{ctx.postal_code ? ` · ${ctx.postal_code}` : ''}</span>
            <span>cart {money(ctx.order_total_eur)} incl. VAT</span>
            {ctx.locale && <span>locale {ctx.locale}</span>}
            <span>{ctx.rates_returned} rate{ctx.rates_returned === 1 ? '' : 's'} returned</span>
            <span>{ctx.elapsed_ms} ms</span>
            <label className="inline-flex items-center gap-1" title="Only used by a shipping experiment: postcode + street decide the A/B arm, like for a customer">
              street
              <input
                value={streetDraft}
                onChange={e => setStreetDraft(e.target.value)}
                onBlur={() => setStreet(streetDraft.trim())}
                onKeyDown={e => { if (e.key === 'Enter') setStreet(streetDraft.trim()); }}
                placeholder="for the experiment arm"
                className="w-40 rounded border border-border bg-background px-1.5 py-0.5 text-[11px] text-foreground"
              />
            </label>
            {!ctx.country_live_at_checkout && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {ctx.what_if ? 'what-if · country not live' : 'not live at checkout'}
              </span>
            )}
          </div>
        )}

        <div className="max-h-[70vh] overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Running the real checkout path…
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </div>
          )}

          {data && !loading && (
            <>
              {/* what the customer sees */}
              {ctx?.what_if && (
                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                  <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5" />
                  <strong>What-if.</strong> {ctx.country} is not switched on at checkout, so customers
                  there see the Shopify delivery profile&rsquo;s own manual rates. Everything below is what
                  Shippy <em>would</em> quote if the country were enabled.
                  <button onClick={() => setTestMode(false)} className="ml-2 underline hover:no-underline">
                    show what customers see today
                  </button>
                </div>
              )}
              {/* running shipping experiment: what BOTH arms pay for this cart */}
              {data.experiment_arms && (
                <div className="mb-3">
                  <ExperimentNotice block={data.experiment_arms} mode="preview" />
                </div>
              )}
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {ctx?.what_if ? 'Would be shown at checkout' : 'Shown at checkout'}
                </h3>
                {ctx && (ctx.carrier_marks_live ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                    visual checkout · logos{ctx.rate_titles_localised ? ' + translated titles' : ''}
                  </span>
                ) : (
                  <span
                    className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                    title={`Carrier logos and the translated second line are live in ${(ctx.mark_countries || []).join(', ') || 'no country'}. Everywhere else the customer sees the plain rate title and Shopify's own description, in English.`}
                  >
                    old checkout · no logos, English titles
                  </span>
                ))}
              </div>
              {data.shown.length === 0 ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                  <AlertTriangle className="mr-1.5 inline h-4 w-4" />
                  Shippy returns no rates for this cart. The customer sees whatever the Shopify
                  delivery profile carries instead.
                  {!ctx?.country_live_at_checkout && !testMode && (
                    <div className="mt-2">
                      <Button size="sm" variant="outline" onClick={() => setTestMode(true)}>
                        Show what Shippy would quote
                      </Button>
                      <span className="ml-2 text-xs opacity-80">
                        {ctx?.country} is not switched on at checkout.
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-border rounded-lg border border-border">
                  {data.shown.map((r, i) => {
                    // In a carrier-mark country the extension draws the second
                    // line itself: a logo plus a sentence in the buyer's
                    // language. Replay it with the extension's own code so this
                    // is what the customer literally sees.
                    const drawn = ctx?.carrier_marks_live
                      ? previewMark({
                          serviceCode: r.service_code || r.code,
                          description: r.description,
                          countryCode: ctx.country_iso2,
                          locale: ctx.locale,
                        })
                      : null;
                    return (
                      <div key={r.code + i} className="flex items-start gap-3 p-3">
                        <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-[3px] border-primary" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="truncate text-sm font-medium">{r.service_name}</span>
                            <span className="shrink-0 text-sm font-semibold tabular-nums">
                              {money(r.price, r.currency)}
                            </span>
                          </div>
                          {/* second line, exactly as checkout renders it */}
                          {drawn ? (
                            <div className="mt-1 flex items-center gap-1.5">
                              {drawn.mark && (
                                <img
                                  src={drawn.mark.src}
                                  alt={drawn.alt}
                                  title={drawn.alt}
                                  className="shrink-0 object-contain"
                                  style={{ height: 14, width: 14 * (drawn.mark.w / drawn.mark.h) }}
                                />
                              )}
                              {drawn.sentence && (
                                <span className="text-xs text-muted-foreground">{drawn.sentence}</span>
                              )}
                            </div>
                          ) : (
                            r.description && (
                              <p className="mt-0.5 text-xs text-muted-foreground">{r.description}</p>
                            )
                          )}
                          {r.reason && (
                            <p className="mt-1 text-[11px] text-muted-foreground/80">{r.reason}</p>
                          )}
                          <code className="mt-1 inline-block rounded bg-muted px-1 text-[10px] text-muted-foreground">{r.code}</code>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* alternative carriers a NON-pilot country would get: same filters
                  as the pilot, never in the response, drawn like checkout would */}
              {data.would_show?.length > 0 && ctx && (
                <>
                  <div className="mb-2 mt-5 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Alternative carriers
                    </h3>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                      feature not switched on in {ctx.country_iso2}
                    </span>
                  </div>
                  <p className="mb-2 text-[11px] leading-relaxed text-muted-foreground">
                    The &ldquo;Standard Shipping via X&rdquo; options are only switched on in {(ctx.alternative_countries || []).join(', ')}.
                    The carriers themselves are live and priced in {ctx.country}; they just are not offered as a
                    separate checkout choice yet. If {ctx.country} were switched on, this cart would also get the
                    options below, at our cost plus the handling fee. Customers in {ctx.country} do <strong>not</strong> see them today.
                  </p>
                  <div className="divide-y divide-border rounded-lg border border-dashed border-amber-300 dark:border-amber-800">
                    {data.would_show.map((r, i) => {
                      const drawn = ctx.carrier_marks_live
                        ? previewMark({
                            serviceCode: r.service_code || r.code,
                            description: r.description,
                            countryCode: ctx.country_iso2,
                            locale: ctx.locale,
                          })
                        : null;
                      return (
                        <div key={r.code + i} className="flex items-start gap-3 p-3">
                          <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-dashed border-amber-400" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="flex min-w-0 items-baseline gap-2">
                                <span className="truncate text-sm font-medium">{r.service_name}</span>
                                <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                  not offered yet
                                </span>
                              </span>
                              <span className="shrink-0 text-sm font-semibold tabular-nums">
                                {money(r.price, r.currency)}
                              </span>
                            </div>
                            {drawn ? (
                              <div className="mt-1 flex items-center gap-1.5">
                                {drawn.mark && (
                                  <img
                                    src={drawn.mark.src}
                                    alt={drawn.alt}
                                    title={drawn.alt}
                                    className="shrink-0 object-contain"
                                    style={{ height: 14, width: 14 * (drawn.mark.w / drawn.mark.h) }}
                                  />
                                )}
                                {drawn.sentence && (
                                  <span className="text-xs text-muted-foreground">{drawn.sentence}</span>
                                )}
                              </div>
                            ) : (
                              r.description && (
                                <p className="mt-0.5 text-xs text-muted-foreground">{r.description}</p>
                              )
                            )}
                            {r.reason && (
                              <p className="mt-1 text-[11px] text-muted-foreground/80">{r.reason}</p>
                            )}
                            {r.detail && (
                              <div className="mt-1 flex flex-wrap gap-x-3 text-[10px] tabular-nums text-muted-foreground/80">
                                {Object.entries(r.detail).map(([k, v]) => (
                                  <span key={k}>{k.replace(/_/g, ' ')}: <strong>{String(v)}</strong></span>
                                ))}
                              </div>
                            )}
                            <code className="mt-1 inline-block rounded bg-muted px-1 text-[10px] text-muted-foreground">{r.code}</code>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* decisions behind the list */}
              {data.decisions?.length > 0 && (
                <>
                  <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Why these prices
                  </h3>
                  <div className="space-y-1.5">
                    {data.decisions.map((d, i) => (
                      <div key={d.code + i} className="flex items-start gap-2 rounded-lg border border-border bg-muted/20 p-2.5">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <p className="text-xs">{d.reason}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* hidden rates */}
              {data.hidden?.length > 0 && (
                <>
                  <button
                    onClick={() => setShowHidden(v => !v)}
                    className="mb-2 mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                  >
                    {showHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    Not shown ({data.hidden.length})
                  </button>
                  {showHidden && (
                    <div className="divide-y divide-border rounded-lg border border-dashed border-border">
                      {data.hidden.map((h, i) => (
                        <div key={h.code + i} className="flex items-start gap-3 p-3 opacity-90">
                          <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-muted-foreground/30" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <span className="truncate text-sm font-medium text-muted-foreground line-through">
                                {h.title || h.code}
                              </span>
                              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                hidden
                              </span>
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground">{h.reason}</p>
                            {h.detail && (
                              <div className="mt-1 flex flex-wrap gap-x-3 text-[10px] tabular-nums text-muted-foreground/80">
                                {Object.entries(h.detail).map(([k, v]) => (
                                  <span key={k}>{k.replace(/_/g, ' ')}: <strong>{String(v)}</strong></span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* caveats */}
              {data.notes?.length > 0 && (
                <div className="mt-5 space-y-2">
                  {data.notes.map((n, i) => (
                    <p key={i} className="rounded-lg border border-border bg-muted/20 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
                      {n}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end border-t border-border p-3">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
